import styles from "./CensusRow.module.css";

import {
  Button,
  Dialog,
  SystemIcon,
  Tooltip,
  Badge,
} from "@justworkshr/milo-components";
import { EmployeeType, DependentType } from "./types";
import { useState } from "react";
import { hasQueryParam } from "utils/helper";

interface CensusRowProps {
  handleDelete: (uuid: string, participantType?: string) => void;
  openDrawer: (initialData?: EmployeeType | DependentType) => () => void;
  employee: EmployeeType | DependentType;
  isCobra?: boolean;
}

const CensusRow = ({
  employee,
  openDrawer,
  handleDelete,
  isCobra,
}: CensusRowProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const v2_dedupe_census_flow = hasQueryParam("usecensustab") && !isCobra;

  const dependentClass = employee.employeeCensusId ? styles.dependentRow : "";

  const missingInfo = !(
    employee.firstName &&
    employee.lastName &&
    employee.gender &&
    employee.dateOfBirth &&
    employee.homeZip
  );

  const isWaiving = employee?.healthInsInfo?.isWaiving ?? false;

  const censusType = employee.participantType
    ? employee.participantType.charAt(0).toUpperCase() +
      employee.participantType.slice(1).toLowerCase()
    : employee.isCompanyOwner
    ? "Owner"
    : "Employee";

  let dateOfBirth: any = employee.dateOfBirth;
  if (dateOfBirth) {
    dateOfBirth = dateOfBirth.split("-");
    dateOfBirth = `${dateOfBirth[1]}/${dateOfBirth[2]}/${dateOfBirth[0]}`;
  }

  const sex = employee.gender
    ? employee.gender.charAt(0).toUpperCase() +
      employee.gender.slice(1).toLowerCase()
    : "";

  const handleDeleteCensus = async (uuid: string, participantType?: string) => {
    await handleDelete(uuid, participantType);
    setIsDeleteDialogOpen(false);
  };

  return (
    <>
      <li
        className={`${styles.censusListRow} ${dependentClass} ${
          styles.alternateRow
        } ${v2_dedupe_census_flow ? styles.sixColumns : ""}`}
      >
        <div className={`${styles.censusListCell} ${styles.nameCell}`}>
          {missingInfo && v2_dedupe_census_flow && (
            <div className={styles.warningIcon}>
              <Tooltip title="Missing employee info, please edit to complete">
                <SystemIcon iconName="warning" size="small" color="error" />
              </Tooltip>
            </div>
          )}
          <span className={styles.employeeName}>
            {employee.firstName} {employee.lastName}
          </span>
          {v2_dedupe_census_flow && isWaiving && (
            <Badge className={styles.isWaivingBadge} children={"Waived"} />
          )}
        </div>
        <div className={styles.censusListCell}>{censusType}</div>
        <div className={styles.censusListCell}>{dateOfBirth || ""}</div>
        {v2_dedupe_census_flow && (
          <div className={styles.censusListCell}>{sex || ""}</div>
        )}
        <div className={styles.censusListCell}>{employee.homeZip || ""}</div>
        <div className={`${styles.employeeActions} ${styles.censusListCell}`}>
          {v2_dedupe_census_flow && !dependentClass && (
            <button
              className={styles.employeeActionButton}
              onClick={openDrawer({
                employeeCensusId: employee.uuid,
                homeState: employee.homeState,
                homeZip: employee.homeZip,
              })}
            >
              <Tooltip title="Add dependent">
                <SystemIcon
                  color="neutral-subtle"
                  size="small"
                  iconName="person-manage"
                />
              </Tooltip>
            </button>
          )}
          {!v2_dedupe_census_flow && !dependentClass && (
            <Button
              className={styles.addDependentButton}
              onClick={openDrawer({
                employeeCensusId: employee.uuid,
                homeState: employee.homeState,
                homeZip: employee.homeZip,
              })}
              variant="ghost"
            >
              Add dependent
            </Button>
          )}
          <button
            className={styles.employeeActionButton}
            onClick={openDrawer(employee)}
          >
            <Tooltip title="Edit">
              <SystemIcon color="neutral-subtle" size="small" iconName="edit" />
            </Tooltip>
          </button>
          {(!v2_dedupe_census_flow || dependentClass) && (
            <button
              className={styles.employeeActionButton}
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Tooltip title="Delete">
                <SystemIcon color="warning" size="small" iconName="trash" />
              </Tooltip>
            </button>
          )}
        </div>
      </li>
      <Dialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Are you sure you want to remove this person?"
        showCloseButton={true}
        primaryButton={
          <Button onClick={() => setIsDeleteDialogOpen(false)} variant="filled">
            Back
          </Button>
        }
        secondaryButton={
          <Button
            onClick={() =>
              handleDeleteCensus(employee.uuid || "", employee.participantType)
            }
            variant="ghost"
          >
            Delete
          </Button>
        }
      >
        <p>
          {v2_dedupe_census_flow
            ? `Any information that’s been added won’t be saved.`
            : `Any information that’s been added won’t be saved. If you’re removing a
          teammate, you’ll also remove any dependents.`}
        </p>
      </Dialog>
    </>
  );
};

export default CensusRow;
